import { PrimaryCare } from '../common/PSXHeader/ObapiDemo/memberProfile';
import { isEniUspMember } from '../common/Utils/memberUtils/memberUtils';
import { isGroupMNRMember } from './user.utils';

// Types
interface PrimaryCareItem {
  depSeqNbr: string;
  lockInIndicator: boolean;
  pcpInfo: PrimaryCare;
}

// Classes
export class PcpOutputConfig {
  showCurrentPcpCard: boolean = false;
  showEmptyPcpMessage: boolean = false;
  showCurrentPcpDescription: boolean = false;
  showChooseOrEditPcpCTA: boolean = false;
  currentPCPLink: string = '';
  showFuturePcpCard: boolean = false;
  showFuturePcpDescription: boolean = false;
  futurePCPLink: string = '';
  showEffectiveStartDate: boolean = false;
  showEffectiveEndDate: boolean = false;
  showCurrentPcpViewCard: boolean = false;
  showFuturePcpViewCard: boolean = false;
  viewIDLink: boolean = false;
  pcpMessage: string = '';
}

class PcpConfig {
  userTypeIdentifier: string = '';
  bypassColumns: string = '';
  isGatedUser: boolean = false;
  isPreEffective: boolean = false;
  hasCurrentPcp: boolean = false;
  hasFuturePcp: boolean = false;
  DNT_outputConfig: string = '{}';
  pcpMessage: string = '';
}

// Constants
const GENERAL_CONFIG = 'GENERAL_CONFIG';
const DEFAULT = 'DEFAULT';
const CNS_INDIANA = 'CNS_INDIANA';
const KELSEY_SEYBOLD = 'KELSEY_SEYBOLD';
const SANITAS = 'SANITAS';

const memberTypeAndTableMapping = {
  'E&I': GENERAL_CONFIG,
  IFP: GENERAL_CONFIG,
  MNR: GENERAL_CONFIG,
  'C&S': GENERAL_CONFIG,
  groupMemberRetiree: DEFAULT,
  studentResource: DEFAULT,
  cnsIndiana: CNS_INDIANA,
  kelseySeybold: KELSEY_SEYBOLD,
  sanitas: SANITAS,
};

// Functions
export const fetchMemberTypeAndLob = (
  selectedMember: any,
  lockInIndicator?: boolean,
  isCnsRestricted?: boolean
): string => {
  if (!selectedMember) return GENERAL_CONFIG;

  if (isGroupMNRMember(selectedMember)) {
    return memberTypeAndTableMapping.groupMemberRetiree;
  }
  // TODO: changes for isStudentResourceMember
  // if (isStudentResourceMember) {
  //   return memberTypeAndTableMapping.studentResource;
  // }

  if (selectedMember?.isCnSMember && (lockInIndicator || isCnsRestricted)) {
    return memberTypeAndTableMapping.cnsIndiana;
  }

  if (selectedMember?.isKelseySeyboldPlan) {
    return memberTypeAndTableMapping.kelseySeybold;
  }

  if (selectedMember?.isSanitasPlan) {
    return memberTypeAndTableMapping.sanitas;
  }

  return (
    memberTypeAndTableMapping[
      selectedMember?.lineOfBusiness as keyof typeof memberTypeAndTableMapping
    ] || GENERAL_CONFIG
  );
};

export const filterPcpConfiguration = (
  selectedMember: any,
  pcpConfig: PcpConfig[],
  primaryCare: PrimaryCareItem[],
  isCnsRestricted?: boolean,
  ENABLE_NEW_PCP_CARE_TEAM_FLOW: Boolean = false,
  isPreEffective: boolean = false
): PcpOutputConfig => {
  const initialConfig = new PcpOutputConfig();
  if (!ENABLE_NEW_PCP_CARE_TEAM_FLOW) return initialConfig;
  if (!selectedMember) return initialConfig;

  const dependentSeqNbr = selectedMember?.demographics?.dependentSeqNbr;
  const pcpInfo = primaryCare?.find(
    ({ depSeqNbr }: any) => dependentSeqNbr === depSeqNbr
  )?.pcpInfo;

  const hasPCP = !!pcpInfo?.hasPCP;
  const hasFuturePCP = pcpInfo?.futurePCP != null;
  const lockInIndicator = primaryCare?.[0]?.lockInIndicator;
  const {
    isGatedUser,
    lineOfBusiness,
    population,
    isNonGatedUSPUser,
    viewPcp,
    editPcp,
    policyNumber,
    reciprocityId,
  } = selectedMember || {};

  const isGatedMember = isEniUspMember(lineOfBusiness, population)
    ? !isNonGatedUSPUser
    : isGatedUser;

  const memberType = fetchMemberTypeAndLob(
    selectedMember,
    lockInIndicator,
    isCnsRestricted
  );

  try {
    const defaultConfig =
      pcpConfig?.find((config) => config?.userTypeIdentifier === DEFAULT) ??
      new PcpConfig();

    const defaultOutputConfig = {
      ...initialConfig,
      ...JSON.parse(defaultConfig.DNT_outputConfig),
    };

    if (memberType === DEFAULT) {
      return defaultOutputConfig;
    }

    const findConfig = (
      filteruserTypeIdentifier: (userTypeIdentifier: string) => boolean
    ): PcpConfig | undefined =>
      pcpConfig?.find((config) => {
        const {
          userTypeIdentifier,
          isGatedUser,
          isPreEffective: preEffectiveConfig,
          hasCurrentPcp,
          hasFuturePcp,
          bypassColumns,
        } = config;

        const policyMatch = filteruserTypeIdentifier(userTypeIdentifier);

        // If no bypassColumns, match all columns
        if (!bypassColumns) {
          return (
            policyMatch &&
            isGatedMember === isGatedUser &&
            isPreEffective === preEffectiveConfig &&
            hasPCP === hasCurrentPcp &&
            hasFuturePCP === hasFuturePcp
          );
        }

        const bypassSet = new Set(
          bypassColumns.split(',').map((col) => col.trim())
        );
        return (
          policyMatch &&
          (bypassSet.has('isGatedUser') || isGatedMember === isGatedUser) &&
          (bypassSet.has('isPreEffective') ||
            isPreEffective === preEffectiveConfig) &&
          (bypassSet.has('hasCurrentPcp') || hasPCP === hasCurrentPcp) &&
          (bypassSet.has('hasFuturePcp') || hasFuturePCP === hasFuturePcp)
        );
      });

    // Helper function to check if userTypeIdentifier matches identifier
    const matchesIdentifier = (
      userTypeIdentifier: string = '',
      identifier: string
    ): boolean =>
      userTypeIdentifier
        .split(',')
        .map((p) => p.trim())
        .includes(identifier);

    const filteredConfig =
      (reciprocityId
        ? findConfig((userTypeIdentifier) =>
            matchesIdentifier(
              userTypeIdentifier,
              `RECIPROCITY_${reciprocityId}`
            )
          )
        : undefined) ??
      findConfig((userTypeIdentifier) =>
        matchesIdentifier(userTypeIdentifier, policyNumber)
      ) ??
      findConfig((userTypeIdentifier) =>
        matchesIdentifier(userTypeIdentifier, `POPULATION_${population ?? ''}`)
      ) ??
      findConfig((userTypeIdentifier) =>
        matchesIdentifier(userTypeIdentifier, memberType)
      ) ??
      findConfig((userTypeIdentifier) =>
        matchesIdentifier(userTypeIdentifier, GENERAL_CONFIG)
      ) ??
      new PcpConfig();

    const outputConfig = {
      ...initialConfig,
      ...JSON.parse(filteredConfig.DNT_outputConfig),
    };

    const finalProcessedConfig = {
      ...defaultOutputConfig,
      ...outputConfig,
      showCurrentPcpCard: outputConfig.showCurrentPcpCard && viewPcp,
      showChooseOrEditPcpCTA: outputConfig.showChooseOrEditPcpCTA && editPcp,
      showFuturePcpCard: outputConfig.showFuturePcpCard && viewPcp,
      pcpMessage: filteredConfig.pcpMessage,
    };

    // eslint-disable-next-line no-console
    console.log(
      '[PCP Config] Matched configuration row from Lagoon table:',
      filteredConfig
    );
    // eslint-disable-next-line no-console
    console.log(
      '[PCP Config] Final processed configuration after applying permissions:',
      finalProcessedConfig
    );

    return finalProcessedConfig;
  } catch {
    return initialConfig;
  }
};
